import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fetchProducts, validateCoupon } from "../lib/api";
import { useAuth } from "./AuthContext";

const ShopCtx = createContext(null);
const STORAGE_KEY = "bindni_shop_v1";
const COUPON_KEY = "bindni_coupon_v1";

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { cart: [], wishlist: [] };
    return JSON.parse(raw);
  } catch {
    return { cart: [], wishlist: [] };
  }
};

const loadCoupon = () => {
  try {
    const raw = localStorage.getItem(COUPON_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const ShopProvider = ({ children }) => {
  const [state, setState] = useState(loadState);
  const [appliedCoupon, setAppliedCouponState] = useState(loadCoupon);
  const { user } = useAuth();
  const hydratedFor = useRef(null);   // user_id we've hydrated wishlist for
  const isHydrating = useRef(false);  // block sync-out while hydrating

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (appliedCoupon) localStorage.setItem(COUPON_KEY, JSON.stringify(appliedCoupon));
    else localStorage.removeItem(COUPON_KEY);
  }, [appliedCoupon]);

  // On login: fetch server wishlist, hydrate local state with union, then allow sync-out.
  useEffect(() => {
    if (!user) {
      hydratedFor.current = null;
      isHydrating.current = false;
      return;
    }
    if (hydratedFor.current === user.user_id) return;
    isHydrating.current = true;
    (async () => {
      try {
        const [wishRes, productsList] = await Promise.all([
          api.get("/me/wishlist", { withCredentials: true }),
          fetchProducts().catch(() => []),
        ]);
        const serverIds = wishRes.data.product_ids || [];
        setState((s) => {
          const localById = new Map(s.wishlist.map((w) => [w.id, w]));
          const productsById = new Map((productsList || []).map((p) => [p.id, p]));
          const merged = [];
          const seen = new Set();
          // Preserve local order first (so device-added items stay on top)
          for (const w of s.wishlist) {
            if (!seen.has(w.id)) { merged.push(w); seen.add(w.id); }
          }
          // Add server-only items using product catalog for details
          for (const id of serverIds) {
            if (seen.has(id)) continue;
            const p = productsById.get(id);
            if (p) {
              merged.push({
                id: p.id, slug: p.slug, name: p.name,
                price: p.price, compare_at_price: p.compare_at_price,
                image: p.images?.[0] || "",
              });
            } else if (localById.has(id)) {
              merged.push(localById.get(id));
            }
            seen.add(id);
          }
          return { ...s, wishlist: merged };
        });
        // Push the merged union back to server (one write, after hydration)
        const unionIds = Array.from(new Set([
          ...serverIds,
          ...state.wishlist.map((w) => w.id),
        ]));
        await api.post("/me/wishlist", { product_ids: unionIds }, { withCredentials: true });
      } catch {}
      hydratedFor.current = user.user_id;
      isHydrating.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Whenever wishlist changes AND user is logged in, sync ids to server (skip while hydrating)
  useEffect(() => {
    if (!user) return;
    if (isHydrating.current) return;
    if (hydratedFor.current !== user.user_id) return;
    const ids = state.wishlist.map((w) => w.id);
    api.post("/me/wishlist", { product_ids: ids }, { withCredentials: true }).catch(() => {});
  }, [state.wishlist, user]);

  const addToCart = (product, qty = 1, variant = null) => {
    setState((s) => {
      const key = `${product.id}::${variant || ""}`;
      const existing = s.cart.find((i) => i.key === key);
      const next = existing
        ? s.cart.map((i) => (i.key === key ? { ...i, quantity: i.quantity + qty } : i))
        : [...s.cart, {
            key,
            product_id: product.id,
            slug: product.slug,
            name: product.name,
            price: product.price,
            image: product.images?.[0] || "",
            variant,
            quantity: qty,
          }];
      return { ...s, cart: next };
    });
    toast.success("Added to cart", { description: product.name });
  };

  const removeFromCart = (key) => {
    setState((s) => ({ ...s, cart: s.cart.filter((i) => i.key !== key) }));
  };

  const updateQty = (key, quantity) => {
    if (quantity <= 0) return removeFromCart(key);
    setState((s) => ({ ...s, cart: s.cart.map((i) => (i.key === key ? { ...i, quantity } : i)) }));
  };

  const clearCart = () => {
    setState((s) => ({ ...s, cart: [] }));
    setAppliedCouponState(null);
  };

  const applyCoupon = async (code) => {
    const c = await validateCoupon(code);
    setAppliedCouponState(c);
    return c;
  };
  const removeCoupon = () => setAppliedCouponState(null);

  const toggleWishlist = (product) => {
    setState((s) => {
      const exists = s.wishlist.find((w) => w.id === product.id);
      if (exists) {
        toast("Removed from wishlist");
        return { ...s, wishlist: s.wishlist.filter((w) => w.id !== product.id) };
      }
      toast.success("Added to wishlist", { description: product.name });
      return {
        ...s,
        wishlist: [...s.wishlist, {
          id: product.id, slug: product.slug, name: product.name,
          price: product.price, compare_at_price: product.compare_at_price,
          image: product.images?.[0] || "",
        }],
      };
    });
  };

  const isInWishlist = (id) => state.wishlist.some((w) => w.id === id);

  const cartCount = state.cart.reduce((a, b) => a + b.quantity, 0);
  const subtotal = state.cart.reduce((a, b) => a + b.price * b.quantity, 0);

  return (
    <ShopCtx.Provider value={{
      ...state, addToCart, removeFromCart, updateQty, clearCart,
      toggleWishlist, isInWishlist, cartCount, subtotal,
      appliedCoupon, applyCoupon, removeCoupon,
    }}>
      {children}
    </ShopCtx.Provider>
  );
};

export const useShop = () => {
  const ctx = useContext(ShopCtx);
  if (!ctx) throw new Error("useShop must be inside ShopProvider");
  return ctx;
};
